import React from 'react';
import { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { getOrderDisplayFromTo } from '@hero/core';
import { VehicleMainIcon, DateIcon } from '@hero/web-icons';
import { VehicleName } from '../../../Vehicle/VehicleName';
import { Collapse } from '@hero/web-components';
import { DetailRow } from './DetailRow';

export interface ShipmentDetailsProps {
  order: Order;
}

export const ShipmentDetails: React.FC<ShipmentDetailsProps> = ({ order }) => {
  const { t, i18n } = useTranslation(['m-order', 'c-dates']);

  const vehicleType = order.load.vehicles[0] || '';

  const translateDate = (key: string, fallback?: string): string => {
    if (key === 'date.today') return t('c-dates:relative_dates.today');
    if (key === 'date.tomorrow') return t('c-dates:relative_dates.tomorrow');
    return t(key, { defaultValue: fallback || key });
  };

  const pickupFromDate = order.dates.pickup;
  const pickupDateDisplay = getOrderDisplayFromTo({
    from: pickupFromDate.from,
    to: pickupFromDate.to,
    translate: translateDate,
    isRTL: i18n.dir() === 'rtl',
  });

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={t('shipment_details.title')}
        icon={
          <div className="p-1.5 bg-blue-50 rounded-lg">
            <VehicleMainIcon className="size-5 text-blue-600" />
          </div>
        }
        defaultOpen={true}>
        <div className="space-y-0">
          <DetailRow
            icon={<VehicleMainIcon className="size-5 text-gray-900" />}
            label={t('shipment_details.truck_type')}
            value={
              <VehicleName
                vehicle={vehicleType}
                className="text-gray-900 text-[14px] font-[500] leading-[22px]"
              />
            }
          />

          <DetailRow
            icon={<DateIcon className="size-5 text-gray-900" />}
            label={t('shipment_details.pickup_date')}
            value={
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-gray-900 text-[14px] font-[500] leading-[22px]">
                  {pickupDateDisplay.dateAutoOnly}
                </span>
                <span className="text-gray-600 text-[12px] font-[400] leading-[18px]">
                  {pickupDateDisplay.timeOnly}
                </span>
              </div>
            }
            showDivider={false}
          />
        </div>
      </Collapse>
    </div>
  );
};
